import React from "react";
import { notify } from "../Notification";
import { PALETTE, PROJECTS } from "../../data/orbstack";
import { Drive, ImageName, Thing, formatAgo, formatSize, lookOf } from "./shared";
import { ReactComponent as TrashIcon } from "../../assets/svgs/sf-symbols/orbstack/trash.svg";
import { ReactComponent as StopIcon } from "../../assets/svgs/sf-symbols/orbstack/stop.fill.svg";
import { ReactComponent as PlayIcon } from "../../assets/svgs/sf-symbols/orbstack/play.fill.svg";
import { ReactComponent as LinkIcon } from "../../assets/svgs/sf-symbols/orbstack/link.svg";
import { ReactComponent as StackIcon } from "../../assets/svgs/sf-symbols/orbstack/square.stack.3d.up.fill.svg";
import { ReactComponent as DisclosureIcon } from "../../assets/svgs/sf-symbols/orbstack/chevron.right.svg";
import { ReactComponent as HelmIcon } from "../../assets/svgs/sf-symbols/orbstack/helm.svg";

// The lists in OrbStack's middle column: each row's icon, name and actions; sections with headers
// that stay at the top while their rows scroll under them.

const Action = ({ icon: Icon, label, onClick, disabled, title, className = "" }) => (
  <button
    type="button"
    className={`ob-action ${className}`}
    disabled={disabled}
    title={title || label}
    aria-label={label}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    <Icon />
  </button>
);

const Row = ({ id, selected, onSelect, icon, name, sub, actions, className = "" }) => (
  <div
    data-id={id}
    role="option"
    aria-selected={selected}
    className={`ob-row ${selected ? "is-selected" : ""} ${className}`}
    onClick={() => onSelect(id)}
  >
    {icon}
    <span className="ob-row-text">
      <span className="ob-row-name">{name}</span>
      {sub && <span className="ob-row-sub">{sub}</span>}
    </span>
    <span className="ob-actions">{actions}</span>
  </div>
);

const Section = ({ title, stuck, children }) => (
  <div className="ob-section">
    {title && (
      <h3 className={`ob-section-title no-select ${stuck[title] ? "is-stuck" : ""}`} data-title={title}>
        {title}
      </h3>
    )}
    {children}
  </div>
);

const inUseSections = (items, used) => [
  { title: "In Use", items: items.filter(used) },
  { title: "Unused", items: items.filter((i) => !used(i)) },
];

export const ImagesList = ({ images, selected, onSelect, onDelete, stuck }) =>
  inUseSections(images, (i) => i.users.length > 0)
    .filter((s) => s.items.length)
    .map((section) => (
      <Section key={section.title} title={section.title} stuck={stuck}>
        {section.items.map((image) => {
          const running = image.users.some((c) => c.running);
          return (
            <Row
              key={image.ref}
              id={image.ref}
              selected={image.ref === selected}
              onSelect={onSelect}
              icon={<Thing look={image.look} />}
              name={<ImageName image={image} />}
              sub={`${formatSize(image.size)}, ${formatAgo(image.created)}`}
              actions={
                <Action
                  icon={TrashIcon}
                  label={`Delete ${image.ref}`}
                  title={running ? "In use by a running container" : "Delete"}
                  disabled={running}
                  onClick={() => onDelete(image)}
                />
              }
            />
          );
        })}
      </Section>
    ));

export const VolumesList = ({ volumes, selected, onSelect, onDelete, stuck }) =>
  inUseSections(volumes, (v) => v.users.length > 0)
    .filter((s) => s.items.length)
    .map((section) => (
      <Section key={section.title} title={section.title} stuck={stuck}>
        {section.items.map((volume) => (
          <Row
            key={volume.name}
            id={volume.name}
            selected={volume.name === selected}
            onSelect={onSelect}
            icon={<Drive look={volume.look} />}
            name={volume.name}
            sub={formatSize(volume.size)}
            className="ob-row--volume"
            actions={
              <Action
                icon={TrashIcon}
                label={`Delete ${volume.name}`}
                title={volume.users.length ? "In use by a container" : "Delete"}
                disabled={volume.users.length > 0}
                onClick={() => onDelete(volume)}
              />
            }
          />
        ))}
      </Section>
    ));

const openLink = () => notify("That opens the container at its local address, which only works on my Mac.");

// A container's row: running ones can be opened, stopped and deleted; stopped ones started.
const ContainerRow = ({ container, images, child, selected, onSelect, onRun, onDelete }) => (
  <Row
    id={container.id}
    selected={container.id === selected}
    onSelect={onSelect}
    className={`ob-row--container ${child ? "ob-row--child" : ""} ${container.running ? "" : "is-stopped"}`}
    icon={<Thing look={lookOf(images, container.image)} status={container.running ? "running" : "stopped"} />}
    name={container.name}
    sub={container.image}
    actions={
      <>
        {container.running && (
          <Action icon={LinkIcon} className="ob-action--link" label={`Open ${container.name}`} onClick={openLink} />
        )}
        {container.running ? (
          <Action
            icon={StopIcon}
            className="ob-action--stop"
            label={`Stop ${container.name}`}
            onClick={() => onRun([container], false)}
          />
        ) : (
          <Action
            icon={PlayIcon}
            className="ob-action--play"
            label={`Start ${container.name}`}
            onClick={() => onRun([container], true)}
          />
        )}
        <Action icon={TrashIcon} label={`Delete ${container.name}`} onClick={() => onDelete([container])} />
      </>
    }
  />
);

// A Compose project: its row opens and closes it, and starts, stops or deletes all of it.
const ProjectRows = ({ project, containers, open, onToggle, ...rest }) => {
  const running = containers.some((c) => c.running);
  return (
    <div role="group" aria-label={project}>
      <div className={`ob-row ob-row--project ${running ? "" : "is-stopped"}`} onClick={() => onToggle(project)}>
        <button
          type="button"
          className={`ob-disclosure ${open ? "is-open" : ""}`}
          aria-expanded={open}
          aria-label={`${open ? "Collapse" : "Expand"} ${project}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(project);
          }}
        >
          <DisclosureIcon />
        </button>
        <StackIcon className="ob-stack" style={{ color: PALETTE[PROJECTS[project] || "gray"] }} aria-hidden="true" />
        <span className="ob-row-text">
          <span className="ob-row-name">{project}</span>
        </span>
        <span className="ob-actions">
          {running ? (
            <Action
              icon={StopIcon}
              className="ob-action--stop"
              label={`Stop ${project}`}
              onClick={() => rest.onRun(containers, false)}
            />
          ) : (
            <Action
              icon={PlayIcon}
              className="ob-action--play"
              label={`Start ${project}`}
              onClick={() => rest.onRun(containers, true)}
            />
          )}
          <Action icon={TrashIcon} label={`Delete ${project}`} onClick={() => rest.onDelete(containers)} />
        </span>
      </div>
      {open &&
        [...containers]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((c) => <ContainerRow key={c.id} container={c} child {...rest} />)}
    </div>
  );
};

// Running projects and containers first, then the stopped ones; projects before lone containers.
export const ContainersList = ({ containers, expanded, searching, stuck, onToggle, ...rest }) => {
  const projects = {};
  const singles = [];
  containers.forEach((c) => (c.project ? (projects[c.project] = projects[c.project] || []).push(c) : singles.push(c)));
  const entries = [
    ...Object.keys(projects)
      .sort()
      .map((project) => ({ key: project, project, containers: projects[project] })),
    ...singles.sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ key: c.id, containers: [c] })),
  ];
  const running = (e) => e.containers.some((c) => c.running);
  const render = (e) =>
    e.project ? (
      <ProjectRows
        key={e.key}
        project={e.project}
        containers={e.containers}
        open={searching || Boolean(expanded[e.project])}
        onToggle={onToggle}
        {...rest}
      />
    ) : (
      <ContainerRow key={e.key} container={e.containers[0]} {...rest} />
    );
  const stopped = entries.filter((e) => !running(e));
  return (
    <>
      <div className="ob-section ob-section--running">{entries.filter(running).map(render)}</div>
      {stopped.length > 0 && (
        <Section title="Stopped" stuck={stuck}>
          {stopped.map(render)}
        </Section>
      )}
    </>
  );
};

export const NoPods = () => (
  <div className="ob-unavailable">
    <div className="ob-unavailable-main">
      <HelmIcon className="ob-unavailable-icon" aria-hidden="true" />
      <p className="ob-unavailable-title">No Pods</p>
    </div>
    <div className="ob-example">
      <p className="ob-example-title">Get started with an example</p>
      <code>kubectl run nginx --image=nginx</code>
    </div>
  </div>
);
